import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { updateZone } from '@/app/Sites/zonesApi';
import { updateSecondaryDevice, fetchSubDevices } from '@/lib/api/devices/devices';
import { updateSpeaker } from '@/lib/api/monitoring/speakers';
import { updateRelay } from '@/lib/api/monitoring/relays';
import { DeviceData } from '../../DevicesTab';
import { MonitoringZoneDto, AssetVideoProcessorDto } from '@/lib/api/types.gen';
import { BlockDto as SiteBlockDto } from '@/app/Sites/siteApi';

interface DropDeviceData {
  deviceType: 'camera' | 'multi-camera' | 'speaker' | 'relay';
  deviceId: string;
  deviceName?: string;
  selectedCameraIds?: string[];
  componentGroupId?: string;
  relatedComponentGroups?: string[];
  enabled?: boolean;
}

interface Site {
  monitoringZones?: MonitoringZoneDto[];
}

interface UseZoneDropHandlerProps {
  monitoredCameras: DeviceData[] | undefined;
  cameras: DeviceData[] | undefined;
  companyId: string;
  siteId: string;
}

export const useZoneDropHandler = ({
  monitoredCameras,
  cameras,
  companyId,
  siteId,
}: UseZoneDropHandlerProps) => {
  const queryClient = useQueryClient();

  const handleZoneDropDevice = useCallback(async (deviceData: DropDeviceData, targetZoneId: string) => {
    try {
      // Get fresh site data from query client
      const freshSite = queryClient.getQueryData(['site', siteId, companyId]) as { monitoringZones?: MonitoringZoneDto[] } | undefined;
      
      if (deviceData.deviceType === 'camera' || deviceData.deviceType === 'multi-camera') {
        
        // STEP 1: Handle zone assignment first (for non-default zones)
        if (targetZoneId !== 'default') {
          
          const targetZone = freshSite?.monitoringZones?.find((zone: MonitoringZoneDto) => zone.id === targetZoneId);
          
          if (targetZone?.id) {
            const currentIds = targetZone.videoProcessors?.map((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId).filter((id): id is string => Boolean(id)) || [];
            const newIds = deviceData.deviceType === 'multi-camera' ? (deviceData.selectedCameraIds || []) : [deviceData.deviceId];
            const updatedIds = [...new Set([...currentIds, ...newIds])];
            
            await updateZone(siteId, targetZone.id!, {
              description: targetZone.description || '',
              videoProcessorIds: updatedIds.filter((id): id is string => Boolean(id)),
              schedule: targetZone.schedule ? {
                ...targetZone.schedule,
                blocks: targetZone.schedule.blocks ? 
                  targetZone.schedule.blocks.map((block): SiteBlockDto => ({
                    dayOfWeek: block.dayOfWeek ?? 0,
                    blockIdx: block.blockIdx ?? 0,
                    active: block.active ?? false
                  })) :
                  undefined
              } : undefined
            });
          }
        }
        
        // STEP 2: Set cameras to monitored=true AFTER zone assignment (skip if already monitored)
        const targetCameraIds = deviceData.deviceType === 'multi-camera' 
          ? (deviceData.selectedCameraIds || []) 
          : [deviceData.deviceId];
        
        // Group cameras by NVR for batch updates
        const camerasByNVR = new Map<string, string[]>();
        
        for (const cameraId of targetCameraIds) {
          const targetCamera = monitoredCameras?.find(cam => cam.id === cameraId) || 
                              cameras?.find(cam => cam.id === cameraId);
          
          if (targetCamera?.nvrId) {
            if (!camerasByNVR.has(targetCamera.nvrId)) {
              camerasByNVR.set(targetCamera.nvrId, []);
            }
            camerasByNVR.get(targetCamera.nvrId)!.push(cameraId);
          }
        }
        
        // Update monitoring status for each NVR using local state + secondary metadata
        for (const [nvrId, cameraIds] of camerasByNVR.entries()) {
          try {
            // Get local UI state for monitoring status (fast)
            const currentPageCameras = (monitoredCameras || []).concat(cameras || [])
              .filter(cam => cam.nvrId === nvrId);
            
            // Fetch secondary devices ONLY for metadata (quillNotes, description, capabilities)
            const secondaryDevices = await fetchSubDevices({ deviceId: nvrId });
            
            if (!currentPageCameras.length || !secondaryDevices?.length) continue;
            
            // Merge local monitoring state with secondary metadata
            const updatedDevices = currentPageCameras.map(camera => {
              const secondaryDevice = secondaryDevices.find(d => d.id === camera.id);
              return {
                id: camera.id,
                monitored: cameraIds.includes(camera.id) ? true : (camera.monitored ?? false),
                // Use secondary API for these metadata fields only
                description: secondaryDevice?.description || null,
                quillNotes: secondaryDevice?.quillNotes || null,
                capabilities: secondaryDevice?.capabilities || {}
              };
            });
            
            // Direct API call with merged data
            await updateSecondaryDevice({
              deviceId: nvrId,
              primaryDeviceId: nvrId,
              devices: updatedDevices
            });
            
          } catch (error) {
            console.error('📦 Error updating camera monitoring status for NVR:', nvrId, error);
          }
        }
        
        // Invalidate and refetch immediately (for all camera drops)
        await queryClient.invalidateQueries({ 
          queryKey: ['siteDevices', { companyId, projectId: siteId }],
          refetchType: 'all'
        });
        await queryClient.invalidateQueries({ 
          queryKey: ['site', siteId, companyId],
          refetchType: 'all'
        });
        
        // Force refetch to ensure fresh data
        await queryClient.refetchQueries({
          queryKey: ['siteDevices', { companyId, projectId: siteId }]
        });
        await queryClient.refetchQueries({
          queryKey: ['site', siteId, companyId]
        });
        
        
      } else if (deviceData.deviceType === 'speaker' || deviceData.deviceType === 'relay') {
        // Handle speaker/relay drop - link to cameras in this zone
        
        // Get cameras in the target zone
        const targetZone = targetZoneId === 'default' ? null : freshSite?.monitoringZones?.find(zone => zone.id === targetZoneId);
        const zoneCameraIds = targetZone?.videoProcessors?.map(vp => vp.videoProcessorConfigId).filter((id): id is string => Boolean(id)) || [];
        
        let cameraOriginalIds: string[] = [];
        
        if (targetZoneId === 'default') {
          // For default zone, link to all cameras not in any specific zone
          const assignedCameraIds = freshSite?.monitoringZones?.flatMap(zone => 
            zone.videoProcessors?.map(vp => vp.videoProcessorConfigId).filter((id): id is string => Boolean(id)) || []
          ) || [];
          cameraOriginalIds = (monitoredCameras?.filter(cam => 
            !assignedCameraIds.includes(cam.id)
          ).map(cam => cam.originalDevice?.id).filter(Boolean) as string[]) || [];
        } else if (zoneCameraIds.length > 0) {
          // For real zones, link to cameras in that zone
          cameraOriginalIds = (monitoredCameras?.filter(cam => 
            zoneCameraIds.includes(cam.id)
          ).map(cam => cam.originalDevice?.id).filter(Boolean) as string[]) || [];
        }
        
        if (cameraOriginalIds.length > 0) {
          if (deviceData.deviceType === 'speaker') {
            await updateSpeaker({
              id: deviceData.deviceId,
              name: deviceData.deviceName || '',
              componentGroupId: deviceData.componentGroupId || deviceData.deviceId,
              relatedComponentGroups: [...new Set([...(deviceData.relatedComponentGroups || []), ...cameraOriginalIds])]
            });
          } else if (deviceData.deviceType === 'relay') {
            await updateRelay({
              id: deviceData.deviceId,
              name: deviceData.deviceName || '',
              componentGroupId: deviceData.componentGroupId || deviceData.deviceId,
              relatedComponentGroups: [...new Set([...(deviceData.relatedComponentGroups || []), ...cameraOriginalIds])]
            });
          }
          
          // Trigger immediate refresh for speaker/relay links
          await queryClient.invalidateQueries({ 
            queryKey: ['siteDevices', { companyId, projectId: siteId }],
            refetchType: 'all'
          });
        }
      }
    } catch (error) {
      console.error('📦 Zone drop error:', error);
    }
  }, [queryClient, siteId, companyId, monitoredCameras, cameras]);

  return { handleZoneDropDevice };
};